import { type FormEvent, useState, useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  CheckCircle,
  Clock3,
  Leaf,
  LockKeyhole,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  Sparkles,
  User,
  Plus,
  Minus,
} from "lucide-react";
import { 
  type PublicTreatmentRetreatPackage,
  bookTreatmentRetreat,
  verifyTreatmentRetreatPayment,
  fetchApplicableCommission
} from "./treatmentRetreatPublicApi";

const toArray = <T,>(value: T[] | null | undefined): T[] =>
  Array.isArray(value) ? value : [];

const formatCurrency = (amount?: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(amount || 0));

interface BookingPreviewState {
  packageData: PublicTreatmentRetreatPackage;
  selectedQuantities: Record<string, number>;
  totalAmount: number;
  baseAmount?: number;
  commissionPercentage?: number | null;
  commissionAmount?: number;
  bookingDate?: string;
}

const WellnessBookingPreview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const stateFromLocation = location.state as BookingPreviewState | null;
  const state: BookingPreviewState | null = useMemo(() => {
    if (stateFromLocation && stateFromLocation.packageData) return stateFromLocation;
    try {
      const saved = sessionStorage.getItem("current_booking_preview");
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return null;
  }, [stateFromLocation]);

  const [customer, setCustomer] = useState({ name: "", email: "", phone: "" });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRazorpayLoaded, setIsRazorpayLoaded] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    const loadRazorpay = () => {
      return new Promise((resolve) => {
        if ((window as any).Razorpay) {
          setIsRazorpayLoaded(true);
          resolve(true);
          return;
        }

        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = () => {
          setIsRazorpayLoaded(true);
          resolve(true);
        };
        script.onerror = () => {
          resolve(false);
        };
        document.body.appendChild(script);
      });
    };

    loadRazorpay();
  }, []);

  const rawPackage = state?.packageData;
  const bookingDate = state?.bookingDate || new Date().toISOString().split("T")[0];
  const pricingOptionsList = useMemo(() => {
    if (!rawPackage) return [];
    const opts = toArray(rawPackage.pricingOptions);
    if (opts.length > 0) return opts;
    return [{ type: "Single", price: rawPackage.price || 3500 }];
  }, [rawPackage]);

  const [selectedQuantities, setSelectedQuantities] = useState<Record<string, number>>(() => {
    if (state?.selectedQuantities && Object.keys(state.selectedQuantities).length > 0) {
      return state.selectedQuantities;
    }
    if (rawPackage) {
      const opts = toArray(rawPackage.pricingOptions);
      if (opts.length > 0) {
        return { [opts[0].type]: 1 };
      }
      return { Single: 1 };
    }
    return { Single: 1 };
  });

  const updateQuantity = (type: string, delta: number) => {
    setSelectedQuantities((prev) => {
      const current = prev[type] ?? 0;
      const next = Math.max(0, current + delta);
      const updated = { ...prev, [type]: next };
      const totalParticipants = Object.values(updated).reduce((a, b) => a + b, 0);
      if (totalParticipants === 0) {
        return prev;
      }
      return updated;
    });
  };

  const selectedOptions = useMemo(() => {
    if (!rawPackage) return [];
    return pricingOptionsList.filter(
      (option) => (selectedQuantities[option.type] ?? 0) > 0
    );
  }, [rawPackage, selectedQuantities]);

  const baseAmount = useMemo(() => {
    if (!rawPackage) return 0;
    if (selectedOptions.length > 0) {
      return selectedOptions.reduce(
        (sum, option) => sum + option.price * (selectedQuantities[option.type] ?? 0),
        0
      );
    }
    return state?.baseAmount || (rawPackage.price || 3500);
  }, [selectedOptions, selectedQuantities, state?.baseAmount, rawPackage]);

  const [commissionRate, setCommissionRate] = useState<number>(() => {
    if (state?.commissionPercentage !== undefined && state?.commissionPercentage !== null && !isNaN(Number(state.commissionPercentage))) {
      return Number(state.commissionPercentage);
    }
    if ((rawPackage as any)?.commissionPercentage !== undefined && (rawPackage as any)?.commissionPercentage !== null && !isNaN(Number((rawPackage as any).commissionPercentage))) {
      return Number((rawPackage as any).commissionPercentage);
    }
    const optComm = (toArray(rawPackage?.pricingOptions)[0] as any)?.commissionPercentage;
    if (optComm !== undefined && optComm !== null && !isNaN(Number(optComm))) {
      return Number(optComm);
    }
    return 0;
  });

  useEffect(() => {
    if (commissionRate === 0 && rawPackage?._id) {
      fetchApplicableCommission({
        serviceId: rawPackage._id,
        vendorId: (rawPackage as any).mentorId,
      }).then((rate) => {
        if (rate !== null && rate > 0) {
          setCommissionRate(rate);
        }
      }).catch(() => {});
    }
  }, [rawPackage, commissionRate]);

  const commissionAmount = useMemo(() => {
    if (commissionRate > 0 && baseAmount > 0) {
      return Math.round(((baseAmount * commissionRate) / 100) * 100) / 100;
    }
    return 0;
  }, [baseAmount, commissionRate]);

  const finalPayableAmount = useMemo(() => {
    return Math.round((baseAmount + commissionAmount) * 100) / 100;
  }, [baseAmount, commissionAmount]);

  if (!state || !state.packageData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[linear-gradient(180deg,#f6fff8_0%,#ffffff_100%)] px-4">
        <div className="max-w-xl rounded-3xl border border-dashed border-emerald-200 bg-white p-10 text-center shadow-sm">
          <h1 className="text-3xl font-bold text-slate-900">No booking selection found</h1>
          <p className="mt-3 text-slate-600">
            Please choose a package and participant options first.
          </p>
          <Link
            to="/luxury-wellness-retreats"
            className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 font-semibold text-white hover:bg-emerald-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to packages
          </Link>
        </div>
      </div>
    );
  }

  const packageData = state.packageData;

  const handleChange = (field: keyof typeof customer, value: string) => {
    setCustomer((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!customer.name || !customer.email || !customer.phone) {
      alert("Please fill in all details");
      return;
    }
    if (!acceptedTerms) {
      alert("Please accept the Terms & Conditions before proceeding with payment.");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const bookingData = {
        packageId: packageData._id,
        packageName: packageData.packageName,
        name: customer.name,
        email: customer.email,
        phone: customer.phone,
        selectedQuantities,
        baseAmount,
        totalAmount: finalPayableAmount,
        commissionRate,
        commissionAmount,
        bookingDate,
      };

      const response = await bookTreatmentRetreat(bookingData) as any;

      if (response && response.data) {
        if (finalPayableAmount === 0) {
           const successState = { 
             bookingId: response.data.bookingId, 
             amount: "0", 
             serviceName: packageData.packageName,
             date: bookingDate ? new Date(bookingDate).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB'),
             duration: `${packageData.duration || ''} ${packageData.startTime && packageData.endTime ? `(${packageData.startTime} - ${packageData.endTime})` : ''}`.trim() || "Flexible",
             location: (packageData.address || [packageData.city, packageData.state].filter(Boolean).join(", ") || "Flexible"),
             customerName: customer.name.trim(),
             customerEmail: customer.email.trim(),
             customerPhone: customer.phone.trim(),
           };
           try {
             sessionStorage.setItem("last_payment_success_state", JSON.stringify(successState));
           } catch (e) {}
           navigate("/payment-success", { state: successState });
           return;
        }

        if (!isRazorpayLoaded || !response.data.razorpayOrderId) {
            alert('Payment system not ready. Please try again.');
            setIsSubmitting(false);
            return;
        }

        if (!process.env.REACT_APP_RAZORPAY_KEY_ID) {
            alert('Razorpay is not configured.');
            setIsSubmitting(false);
            return;
        }

        const options = {
            key: process.env.REACT_APP_RAZORPAY_KEY_ID,
            amount: response.data.amount,
            currency: response.data.currency,
            name: 'Metafit Wellness',
            description: `Booking for ${packageData.packageName}`,
            image: '/metalogo.webp',
            order_id: response.data.razorpayOrderId,
            handler: async (razorpayResponse: any) => {
                const paymentData = {
                    ...razorpayResponse,
                    bookingId: response.data.bookingId,
                    pendingBookingData: response.data.pendingBookingData
                };

                try {
                    const verificationRes = await verifyTreatmentRetreatPayment(paymentData) as any;
                    if (verificationRes.success) {
                        const successState = { 
                          bookingId: response.data.bookingId, 
                          amount: finalPayableAmount.toString(), 
                          serviceName: packageData.packageName,
                          date: bookingDate ? new Date(bookingDate).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB'),
                          duration: `${packageData.duration || ''} ${packageData.startTime && packageData.endTime ? `(${packageData.startTime} - ${packageData.endTime})` : ''}`.trim() || "Flexible",
                          location: (packageData.address || [packageData.city, packageData.state].filter(Boolean).join(", ") || "Flexible"),
                          customerName: customer.name.trim(),
                          customerEmail: customer.email.trim(),
                          customerPhone: customer.phone.trim(),
                        };
                        try {
                          sessionStorage.setItem("last_payment_success_state", JSON.stringify(successState));
                        } catch (e) {}
                        navigate("/payment-success", { state: successState });
                    } else {
                        alert("Payment verification failed. Please contact support.");
                        setIsSubmitting(false);
                    }
                } catch (error: any) {
                    alert('An error occurred during payment verification.');
                    setIsSubmitting(false);
                }
            },
            prefill: {
                name: customer.name,
                email: customer.email,
                contact: customer.phone,
            },
            notes: {
                bookingId: response.data.bookingId,
            },
            theme: {
                color: '#059669', // emerald-600
            },
            modal: {
                ondismiss: () => {
                    alert('Payment was cancelled.');
                    setIsSubmitting(false);
                }
            }
        };

        const rzp = new (window as any).Razorpay(options);
        rzp.open();

        rzp.on('payment.failed', function (response: any) {
            alert(`Payment failed: ${response.error.description}`);
            setIsSubmitting(false);
        });
      } else {
          alert('Failed to start booking.');
          setIsSubmitting(false);
      }
    } catch (error) {
      console.error(error);
      alert("Something went wrong! Try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#FAF7F2] text-[#15302B] antialiased w-full max-w-full overflow-x-hidden">
      {/* Background ambient glow */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-[radial-gradient(ellipse_at_top,rgba(21,48,43,0.05),transparent_70%)]" />

      <main className="relative mx-auto max-w-[1180px] w-full min-w-0 px-3.5 pt-4 pb-16 sm:px-6 sm:pt-6 sm:pb-20 lg:px-8 box-border">
        {/* Back Button (Hidden on Mobile, Visible on Desktop/Tablet) */}
        <div className="mb-3 sm:mb-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="group inline-flex items-center gap-1.5 rounded-full border border-[#15302B]/15 bg-white px-3.5 py-1.5 text-xs font-semibold text-[#15302B] shadow-2xs transition-all hover:bg-[#F2F8F4] hover:shadow-sm cursor-pointer"
          >
            <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-1" />
            <span>Back</span>
          </button>
        </div>

        {/* Page Header */}
        <div className="mb-5 sm:mb-6 border-b border-[#15302B]/10 pb-4 sm:pb-5">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
            <div>
              <h1 className="font-serif text-xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#15302B]">
                Complete Your Booking
              </h1>
            </div>

            <div className="hidden sm:inline-flex items-center gap-2 rounded-full border border-[#15302B]/12 bg-white px-3.5 py-1.5 text-xs font-semibold text-[#2D6A4F] shadow-2xs self-start sm:self-auto shrink-0">
              <ShieldCheck className="h-4 w-4 text-[#2D6A4F]" />
              <span>100% Secure Checkout</span>
            </div>
          </div>
        </div>

        {/* 2-Column Responsive Layout */}
        <div className="grid items-stretch gap-5 sm:gap-6 lg:grid-cols-2 w-full min-w-0">
          {/* Left Column: Guest Details (First on mobile & desktop) */}
          <div className="lg:col-span-1 flex flex-col w-full min-w-0">
            <div className="h-full flex flex-col justify-between overflow-hidden rounded-2xl border border-[#15302B]/10 bg-white shadow-sm transition-shadow hover:shadow-md w-full min-w-0 box-border">
              {/* Card Header */}
              <div className="flex items-center justify-between border-b border-[#15302B]/8 bg-[#FAF7F2]/60 px-4 py-3 sm:px-5 sm:py-3.5">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full bg-[#15302B] text-white shadow-xs">
                    <User className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h2 className="font-serif text-sm sm:text-lg font-bold text-[#15302B]">
                      Primary Guest Details
                    </h2>
                  </div>
                </div>
                <span className="hidden items-center gap-1 text-[11px] font-medium text-[#3F6652] sm:inline-flex">
                  <LockKeyhole className="h-3 w-3" />
                  Encrypted
                </span>
              </div>

              {/* Card Form */}
              <div className="p-3.5 sm:p-5 flex-1 flex flex-col justify-between w-full min-w-0 box-border">
                {submitted ? (
                  <div className="rounded-2xl border border-[#3F6652]/20 bg-[#F2F8F4] p-6 text-center">
                    <CheckCircle className="mx-auto h-8 w-8 text-[#3F6652]" />
                    <p className="mt-2 font-serif text-lg font-bold text-[#15302B]">
                      Booking details submitted!
                    </p>
                    <p className="mt-1 text-xs text-[#4A5568]">
                      We received your details and have initiated your confirmation.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="flex-1 flex flex-col justify-between space-y-4">
                  <div className="space-y-4">
                    {/* Full Name */}
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold text-[#15302B]">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <User className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#3F6652]" />
                        <input
                          required
                          type="text"
                          autoComplete="name"
                          value={customer.name}
                          onChange={(e) => handleChange("name", e.target.value)}
                          className="w-full rounded-xl border border-[#15302B]/15 bg-white py-2.5 pl-10 pr-4 text-xs font-medium text-[#15302B] shadow-2xs outline-none transition-all placeholder:text-[#94A3B8] focus:border-[#3F6652] focus:ring-2 focus:ring-[#3F6652]/20"
                          placeholder="e.g. Aditi Sharma"
                        />
                      </div>
                    </div>

                    {/* Email Address */}
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold text-[#15302B]">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#3F6652]" />
                        <input
                          required
                          type="email"
                          autoComplete="email"
                          value={customer.email}
                          onChange={(e) => handleChange("email", e.target.value)}
                          className="w-full rounded-xl border border-[#15302B]/15 bg-white py-2.5 pl-10 pr-4 text-xs font-medium text-[#15302B] shadow-2xs outline-none transition-all placeholder:text-[#94A3B8] focus:border-[#3F6652] focus:ring-2 focus:ring-[#3F6652]/20"
                          placeholder="aditi@example.com"
                        />
                      </div>
                    </div>

                    {/* Phone Number */}
                    <div>
                      <label className="mb-1.5 block text-xs font-semibold text-[#15302B]">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#3F6652]" />
                        <input
                          required
                          type="tel"
                          autoComplete="tel"
                          value={customer.phone}
                          onChange={(e) => handleChange("phone", e.target.value)}
                          className="w-full rounded-xl border border-[#15302B]/15 bg-white py-2.5 pl-10 pr-4 text-xs font-medium text-[#15302B] shadow-2xs outline-none transition-all placeholder:text-[#94A3B8] focus:border-[#3F6652] focus:ring-2 focus:ring-[#3F6652]/20"
                          placeholder="+91 98765 43210"
                        />
                      </div>
                    </div>

                  </div>

                  </form>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Order Summary Sidebar (Second / Baad me on mobile & desktop) */}
          <aside className="lg:col-span-1 flex flex-col w-full min-w-0">
            <div className="h-full flex flex-col justify-between overflow-hidden rounded-2xl border border-[#15302B]/10 bg-white shadow-sm transition-shadow hover:shadow-md w-full min-w-0 box-border">

              <div className="p-3.5 sm:p-5 flex-1 flex flex-col justify-between space-y-4 w-full min-w-0 box-border">
                <div className="space-y-3.5">
                {/* Package Info Header */}
                <div>
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1 font-mono text-[9px] font-bold uppercase tracking-[0.14em] text-[#3F6652]">
                      <Leaf className="h-2.5 w-2.5" /> Booking Summary
                    </span>
                    <span className="rounded-full bg-[#3F6652]/10 px-2 py-0.5 text-[9px] font-semibold text-[#15302B]">
                      Verified Retreat
                    </span>
                  </div>

                  <h3 className="mt-1 font-serif text-[13.5px] sm:text-[14.5px] font-bold text-[#15302B] leading-tight">
                    {packageData.packageName}
                  </h3>

                  <div className="mt-1.5 space-y-1 text-[11px] text-[#4A5568]">
                    <p className="flex items-center gap-1.5">
                      <Clock3 className="h-3 w-3 text-[#3F6652] shrink-0" />
                      <span>
                        {packageData.duration ? `${packageData.duration} stay` : "Flexible duration"}
                      </span>
                      {packageData.startTime && packageData.endTime && (
                        <span className="text-[10px] text-[#718096]">
                          ({packageData.startTime} - {packageData.endTime})
                        </span>
                      )}
                    </p>
                    <p className="flex items-start gap-1.5 min-w-0">
                      <MapPin className="h-3.5 w-3.5 text-[#3F6652] shrink-0 mt-0.5" />
                      <span className="min-w-0 flex-1 break-words leading-tight">
                        {[packageData.address, packageData.city, packageData.state].filter(Boolean).join(", ") ||
                          packageData.TreatmentOrganiser ||
                          "Location details upon booking"}
                      </span>
                    </p>
                    {bookingDate && (
                      <p className="flex items-center gap-1.5 text-[#15302B] font-medium min-w-0 flex-wrap">
                        <Calendar className="h-3 w-3 text-[#3F6652] shrink-0" />
                        <span className="min-w-0 break-words">
                          Treatment Date: <strong className="text-[#2D6A4F]">{new Date(bookingDate).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}</strong>
                        </span>
                      </p>
                    )}
                  </div>
                </div>

                <div className="border-t border-[#15302B]/8" />

                {/* Price Breakdown */}
                <div className="space-y-2 text-xs w-full min-w-0">
                  {selectedOptions.length > 0 ? (
                    selectedOptions.map((option) => (
                      <div
                        key={option.type}
                        className="flex justify-between items-start gap-2 text-[#4A5568] min-w-0"
                      >
                        <span className="min-w-0 flex-1 break-words">
                          {option.type.toLowerCase() === "single"
                            ? `${selectedQuantities[option.type]} participant(s) × ${formatCurrency(option.price)}`
                            : `${option.type} (${selectedQuantities[option.type]} participant(s) × ${formatCurrency(option.price)})`}
                        </span>
                        <span className="font-mono font-medium shrink-0 whitespace-nowrap text-right">
                          {formatCurrency(option.price * (selectedQuantities[option.type] ?? 0))}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="flex justify-between text-[#4A5568]">
                      <span>Base Amount</span>
                      <span className="font-mono font-medium">{formatCurrency(baseAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center gap-2 text-[#4A5568] min-w-0">
                    <span className="min-w-0 flex-1 break-words">Taxes & Platform Fees</span>
                    {commissionRate > 0 ? (
                      <span className="font-semibold text-[#3F6652] shrink-0 whitespace-nowrap text-right">
                        Included{commissionAmount > 0 ? ` (+${formatCurrency(commissionAmount)})` : ''}
                      </span>
                    ) : (
                      <span className="font-semibold text-[#B91C1C] shrink-0 whitespace-nowrap text-right">
                        Not Included
                      </span>
                    )}
                  </div>
                  <div className="border-t border-[#15302B]/8 pt-3 flex items-baseline justify-between gap-2 min-w-0">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold text-[#15302B]">Total Payable</p>
                      <p className="text-[10px] text-[#718096] break-words">
                        {commissionRate > 0 ? "Inclusive of all charges" : "Exclusive of taxes & platform fees"}
                      </p>
                    </div>
                    <p className="font-serif text-lg sm:text-xl font-bold text-[#15302B] shrink-0 whitespace-nowrap text-right">
                      {formatCurrency(finalPayableAmount)}
                    </p>
                  </div>
                </div>

                </div>
                <div className="space-y-3 mt-auto pt-2">
                {/* Action Button & Terms Checkbox */}
                {!submitted && (
                  <div className="space-y-3">
                    <div className="flex items-start gap-2 bg-[#FAF7F2] p-2.5 sm:p-3 rounded-xl border border-[#15302B]/8 w-full min-w-0">
                      <input
                        type="checkbox"
                        id="terms-desktop"
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                        className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-[#15302B] focus:ring-[#15302B] cursor-pointer accent-[#15302B]"
                      />
                      <label htmlFor="terms-desktop" className="text-xs text-[#15302B] select-none cursor-pointer leading-tight min-w-0 flex-1 break-words">
                        I accept the{" "}
                        <a
                          href="/policy/terms-conditions"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-bold underline hover:text-[#2C4A3B]"
                        >
                          Terms & Conditions
                        </a>
                      </label>
                    </div>

                    <button
                      type="button"
                      onClick={() => document.querySelector<HTMLFormElement>("form")?.requestSubmit()}
                      disabled={isSubmitting || !acceptedTerms || (finalPayableAmount > 0 && !isRazorpayLoaded)}
                      className="w-full flex items-center justify-center gap-2 rounded-full bg-black py-2.5 px-5 text-xs sm:text-[13px] font-semibold text-white shadow-sm transition-all hover:bg-neutral-800 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    >
                      <LockKeyhole className="h-4 w-4" />
                      {isSubmitting
                        ? "Processing..."
                        : finalPayableAmount === 0
                        ? "Confirm Booking"
                        : `Pay ${formatCurrency(finalPayableAmount)}`}
                    </button>
                  </div>
                )}

                <div className="flex items-center justify-center gap-1.5 text-[11px] text-[#4A5568]">
                  <CheckCircle className="h-3.5 w-3.5 text-[#3F6652]" />
                  <span>Instant confirmation after payment</span>
                </div>
              </div>
                </div>
            </div>

          </aside>
        </div>
      </main>
    </div>
  );
};

export default WellnessBookingPreview;
